import _ from "lodash";
import { colors, createMuiTheme, responsiveFontSizes } from "@material-ui/core";
import { THEMES } from "src/constants";
import { softShadows, strongShadows } from "./shadows";
import typography from "./typography";

const baseOptions = {
  direction: "ltr",
  typography,
  overrides: {
    MuiLinearProgress: {
      root: {
        borderRadius: 3,
        overflow: "hidden"
      }
    },
    MuiListItemIcon: {
      root: {
        minWidth: 32
      }
    },
    MuiChip: {
      root: {
        backgroundColor: "rgba(0,0,0,0.075)"
      }
    },
    MuiButton: {
      root: {
        textTransform: "inherit",
        borderRadius: "8px",
        minHeight: "46px",
        minWidth: "100px"
      },
      defaultProps: {
        size: "small",
        textTransform: "inherit"
      }
    },
    MuiTabs: {
      // Folder/boxed tabs: the sliding underline indicator is hidden (the
      // selected tab's box replaces it). No baseline on the Tabs root — its
      // overflow:hidden would clip the selected tab above the border and leave a
      // visible line between the tab and content. The content panel's borders
      // form the enclosure instead, so the selected tab merges into it.
      root: {
        // Match the tab min-height so the selected tab box reaches the content
        // panel (no gap between the tab row and the panel).
        minHeight: "44px"
      },
      indicator: {
        display: "none"
      }
    },
    MuiTab: {
      // Folder/boxed tab style — the app-wide default for all tabs.
      root: {
        textAlign: "left",
        textTransform: "none",
        color: "#607D8B",
        minHeight: "44px",
        marginRight: "6px",
        border: "1px solid transparent",
        borderBottom: "none",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        marginBottom: "-1px",
        "&.Mui-selected": {
          color: "#263238",
          borderColor: "#d0d5d9",
          borderTop: "3px solid #212B36"
        }
      },
      wrapper: {
        alignItems: "flex-start"
      }
    },
    MuiFilledInput: {
      defaultProps: {
        margin: "dense"
      }
    },
    MuiFormControl: {
      defaultProps: {
        margin: "dense"
      }
    },
    MuiFormHelperText: {
      defaultProps: {
        margin: "dense"
      }
    },
    MuiIconButton: {
      defaultProps: {
        size: "small"
      }
    },
    MuiInputBase: {
      defaultProps: {
        margin: "dense"
      }
    },
    MuiInputLabel: {
      defaultProps: {
        margin: "dense"
      }
    },
    MuiListItem: {
      defaultProps: {
        dense: true
      }
    },
    MuiOutlinedInput: {
      defaultProps: {
        margin: "dense"
      }
    },
    MuiFab: {
      defaultProps: {
        size: "small"
      }
    },
    MuiTable: {
      defaultProps: {
        size: "small"
      }
    },
    MuiTextField: {
      defaultProps: {
        margin: "dense"
      }
    },
    MuiToolbar: {
      defaultProps: {
        variant: "dense"
      }
    },
    MuiPaper: {
      rounded: {
        borderRadius: "8px"
      }
    },
    MuiAccordion: {
      root: {
        "&::before": {
          opacity: 0
        }
      },
      rounded: {
        borderRadius: "8px",
        marginBottom: "16px"
      }
    },
    MuiTooltip: {
      // The v4 default is a 10px, tightly-padded grey chip — too small to read and
      // far too cramped for the explanatory tooltips the app uses. Colours are set
      // per-theme in the second pass below.
      tooltip: {
        borderRadius: "8px",
        padding: "8px 12px",
        fontSize: "12.5px",
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: 0,
        maxWidth: 340
      }
    }
  },
  props: {
    MuiTooltip: {
      // An arrow ties the tooltip to what it describes; a short delay stops them
      // flickering open while the pointer crosses a dense table.
      arrow: true,
      enterDelay: 200,
      leaveDelay: 0
    },
    MuiSelect: {
      MenuProps: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left"
        },
        getContentAnchorEl: null
      }
    }
  }
};

const themesOptions = [
  {
    name: THEMES.LIGHT,
    overrides: {
      MuiInputBase: {
        input: {
          "&::placeholder": {
            opacity: 1,
            color: colors.blueGrey[600]
          }
        }
      },
      MuiCheckbox: { 
        colorSecondary: {
          color: colors.blueGrey[900],
          '&$checked': {
            color: colors.blueGrey[900],
          },
        },
      },
    },
    palette: {
      type: "light",
      action: {
        active: colors.blueGrey[500]
      },
      background: {
        default: colors.common.white,
        dark: "#f4f6f8",
        paper: colors.common.white
      },
      primary: {
        main: "#212B36"
      },
      secondary: {
        main: colors.common.white,
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[500]
      }
    },
    shadows: softShadows
  },
  {
    name: THEMES.ONE_DARK,
    palette: {
      type: "dark",
      action: {
        active: "rgba(255, 255, 255, 0.54)",
        hover: "rgba(255, 255, 255, 0.04)",
        selected: "rgba(255, 255, 255, 0.08)",
        disabled: "rgba(255, 255, 255, 0.26)",
        disabledBackground: "rgba(255, 255, 255, 0.12)",
        focus: "rgba(255, 255, 255, 0.12)"
      },
      background: {
        default: "#282C34",
        dark: "#1c2025",
        paper: "#282C34"
      },
      primary: {
        main: "#8a85ff"
      },
      secondary: {
        main: "#0070bd"
      },
      text: {
        primary: "#e6e5e8",
        secondary: "#adb0bb"
      }
    },
    shadows: strongShadows
  },
  {
    name: THEMES.UNICORN,
    palette: {
      type: "dark",
      action: {
        active: "rgba(255, 255, 255, 0.54)",
        hover: "rgba(255, 255, 255, 0.04)",
        selected: "rgba(255, 255, 255, 0.08)",
        disabled: "rgba(255, 255, 255, 0.26)",
        disabledBackground: "rgba(255, 255, 255, 0.12)",
        focus: "rgba(255, 255, 255, 0.12)"
      },
      background: {
        default: "#2a2d3d",
        dark: "#222431",
        paper: "#2a2d3d"
      },
      primary: {
        main: "#a67dff"
      },
      secondary: {
        main: "#a67dff"
      },
      text: {
        primary: "#f6f5f8",
        secondary: "#9699a4"
      }
    },
    shadows: strongShadows
  }
];

export const createTheme = (config = {}) => {
  let themeOptions = themesOptions.find(theme => theme.name === config.theme);

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions;
  }

  let theme = createMuiTheme(
    _.merge({}, baseOptions, themeOptions, { direction: config.direction })
  );

  // Tooltips use an inverted surface so they lift off the page in either theme:
  // near-black on light, a raised slate (plus a hairline edge) on dark, where a
  // flat dark chip would disappear into the background.
  const isDark = theme.palette.type === "dark";
  const tooltipBg = isDark ? "#39414d" : "#212B36";
  const tooltipFg = isDark ? "#f6f5f8" : "#ffffff";

  // Make the folder-tab colors follow the active theme — baseline, borders, top
  // accent, selected surface, and text — so tabs read correctly in every theme.
  theme = createMuiTheme(theme, {
    overrides: {
      MuiTooltip: {
        tooltip: {
          backgroundColor: tooltipBg,
          color: tooltipFg,
          boxShadow: isDark
            ? "0 6px 20px rgba(0, 0, 0, 0.45)"
            : "0 6px 20px rgba(5, 44, 101, 0.22)",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.09)" : "none"
        },
        arrow: {
          color: tooltipBg
        }
      },
      MuiTabs: {
        // Recessed tab-header strip so the selected (paper) tab pops and connects
        // to the content, instead of a flat white header. Rounded top corners so
        // the strip matches a rounded container (e.g. the dashboard Paper card).
        root: {
          backgroundColor: theme.palette.background.dark,
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px"
        }
      },
      MuiTab: {
        root: {
          color: theme.palette.text.secondary,
          // Unselected tabs sit on a recessed surface; the selected tab overrides
          // to background.paper so it reads as the "open" folder tab.
          backgroundColor: theme.palette.background.dark,
          "&.Mui-selected": {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
            borderTop: `3px solid ${theme.palette.primary.main}`
          }
        }
      },
      MuiTabPanel: {
        // Enclose tab content so it connects to the tab row (proper tab control).
        // Same surface as the selected tab so the active tab merges into the panel.
        root: {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderTop: "none",
          borderBottomLeftRadius: "8px",
          borderBottomRightRadius: "8px",
          padding: theme.spacing(2)
        }
      }
    }
  });

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
